<script lang="ts">
  import { onMount, tick, untrack } from 'svelte';
  import {
    BOTTOM_SHEET_CLOSE_DURATION,
    closeBottomSheet,
    getBottomSheetComponent,
    getBottomSheetData,
    getBottomSheetIsOpen,
  } from '$components/bottom-sheet/index.svelte';
  import gsap from 'gsap';
  import { browser } from '$app/environment';
  import { detectSwipeEvent } from '$utils/detectSwipeEvent';

  interface Props {
    presented?: boolean;
    onOpen?: () => void;
  }

  let { presented = $bindable(false), onOpen }: Props = $props();

  const TOP_NEGATIVE_SPACE = 40;

  const CONTENT_CONTAINER_HEIGHT = 'max-h-[95vh]';

  const PAN_THRESHOLD = 100;

  let closedBy: 'overlay' | 'swipe' | undefined;

  let bottomSheetElement = $state<HTMLDivElement | null>(null);

  let bottomSheetWrapper = $state<HTMLDivElement | null>(null);

  let contentElement = $state<HTMLDivElement | null>(null);

  let handle = $state<HTMLDivElement | null>(null);

  let overlayElement = $state<HTMLButtonElement | null>(null);

  let resizeObserver: ResizeObserver | null | undefined;

  onMount(async () => {
    if (!browser) {
      return;
    }

    if (bottomSheetWrapper) {
      detectSwipeEvent(bottomSheetWrapper, () => {
        close();
      });

      window.onpopstate = function (_) {
        close();
      };
    }
  });

  const open: () => void = async () => {
    presented = true;

    document.body.classList.add('overflow-hidden');

    await tick();

    createResizeObserver();

    onOpen?.();
  };

  const closeByOverlay = () => {
    closedBy = 'overlay';

    close();
  };

  const close = async (velocity?: number): Promise<void> => {
    if (presented) {
      void closeBottomSheet();
    } else if (closedBy) {
      return;
    }

    const onclose = getBottomSheetData()?.onclose;
    if (onclose) {
      onclose();
    }

    if (bottomSheetElement) {
      if (closedBy === 'swipe') {
        gsap.to(bottomSheetElement, {
          duration: BOTTOM_SHEET_CLOSE_DURATION / 1000 / Math.max(velocity ?? 0, 1),
          ease: 'power2.out',
          y: 0,
        });
        gsap.to(overlayElement, {
          duration: 0.3,
          delay: 0.1,
          opacity: 0,
        });
      } else {
        gsap.to(bottomSheetElement, {
          duration: BOTTOM_SHEET_CLOSE_DURATION * 0.001,
          ease: 'power3.inOut',
          y: 0,
        });
        gsap.to(overlayElement, {
          duration: BOTTOM_SHEET_CLOSE_DURATION * 0.001 * 0.7,
          delay: 0.1,
          opacity: 0,
        });
      }
    }

    document.body.classList.remove('overflow-hidden');
    presented = false;

    await tick();

    closedBy = undefined;
    disconnectResizeObserver();
  };

  const handleResize = (): void => {
    if (!contentElement || !bottomSheetElement) {
      return;
    }

    const height = getContentHeight();

    if (!height) {
      close();
    } else {
      gsap.to(bottomSheetElement, {
        duration: 0.45,
        ease: 'power3.out',
        y: isModal ? 0 - height - 40 : 0 - height,
      });
      gsap.to(overlayElement, {
        duration: 0.3,
        opacity: 0.45,
      });
    }
  };

  const getContentHeight = (): number => {
    if (!contentElement || !bottomSheetElement) {
      return 0;
    }

    let newHeight = contentElement?.clientHeight ?? 0;

    const MAX_HEIGHT = window.innerHeight - TOP_NEGATIVE_SPACE;

    if (newHeight > MAX_HEIGHT) {
      newHeight = MAX_HEIGHT;
    }

    return newHeight;
  };

  const createResizeObserver = (): void => {
    if (!contentElement) {
      return;
    }

    resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(contentElement);
    resizeObserver.observe(document.body);
  };

  const disconnectResizeObserver = (): void => {
    if (!resizeObserver) {
      return;
    }
    resizeObserver.disconnect();
  };

  onMount(async () => {
    const { default: Hammer } = await import('hammerjs');

    if (handle) {
      const hammer = new Hammer(handle);

      hammer.get('pan').set({ direction: Hammer.DIRECTION_VERTICAL });

      let startY: number | null;
      let initialPosition: number;
      let currentBottomSheetPosition = 0;

      hammer.on('panstart', (event) => {
        startY = event.center.y;
        initialPosition = 0 - getContentHeight();
      });

      hammer.on('panmove', (event) => {
        const deltaY = event.center.y - (startY ?? 0);
        currentBottomSheetPosition = deltaY + initialPosition;

        currentBottomSheetPosition = Math.max(currentBottomSheetPosition, initialPosition);

        const progress = Math.min(1, Math.max(1 - deltaY / (0 - initialPosition), 0));
        gsap.to(bottomSheetElement, {
          y: currentBottomSheetPosition,
          duration: 0.01,
        });
        gsap.to(overlayElement, {
          opacity: 0.45 * progress,
          duration: 0.01,
        });
      });

      hammer.on('panend', (e) => {
        startY = null;

        if (currentBottomSheetPosition < initialPosition + PAN_THRESHOLD) {
          currentBottomSheetPosition = initialPosition;
          gsap.to(bottomSheetElement, {
            y: initialPosition,
            ease: 'power3.out',
            duration: 0.2,
          });
          gsap.to(overlayElement, {
            opacity: 0.45,
            duration: 0.2,
          });
        } else {
          closedBy = 'swipe';
          close(e.velocityY);
        }
      });
    }
  });

  const syncState = (shouldOpen: boolean): void => {
    if (shouldOpen) {
      open();
    } else {
      close();
    }
  };

  let isModal = $derived(getBottomSheetData()?.isModal);
  let transparent = $derived(getBottomSheetData()?.transparent);
  let backdropBlur = $derived(getBottomSheetData()?.backdropBlur);

  $effect(() => {
    const isOpen = getBottomSheetIsOpen();
    if (isOpen === false || isOpen === true) {
      untrack(() => syncState(isOpen));
    }
  });

  const SvelteComponent = $derived(getBottomSheetComponent());
</script>

<div
  id="bottom-sheet"
  style="view-transition-name: bottom-sheet;"
  class="DESKTOP_VIEWPORT fixed inset-0 z-60 h-screen"
  class:pointer-events-none={!presented}
  class:backdrop-blur-md={backdropBlur}
  bind:this={bottomSheetWrapper}>
  <button
    aria-label="Close"
    class="absolute left-0 top-0 h-full w-full opacity-0"
    class:bg-black={!transparent}
    onclick={() => closeByOverlay()}
    bind:this={overlayElement}
    data-testid="bottom-sheet-overlay-button"></button>

  <div
    bind:this={bottomSheetElement}
    class="relative left-0 top-full overflow-y-auto rounded-t-3xl bg-background dark:bg-background-subtle {CONTENT_CONTAINER_HEIGHT}"
    class:bg-transparent={transparent}
    class:dark:bg-transparent={transparent}
    class:shadow-xl={!transparent}
    class:rounded-b-3xl={isModal && !transparent}
    class:w-full={!isModal}
    class:mx-4={isModal}
    class:h-screen={!isModal}>
    <div class="absolute inset-0 h-[20px] w-full p-2">
      <div
        class="w-18 mx-auto h-full rounded-full bg-background-on-subtle"
        class:bg-transparent={transparent}>
      </div>
    </div>
    <div bind:this={contentElement} class=" max-h-full overflow-y-auto" class:pb-safe={!isModal}>
      <SvelteComponent></SvelteComponent>
    </div>
    <div class="absolute inset-0 z-1 h-full w-full" bind:this={handle}></div>
  </div>
</div>
